// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { usePlanDetailContext } from './PlanDetailContext';
import { EmptyState } from '../../../components/shell/ScreenHelpers';
import PlanTimeSeriesChart from '../../../components/charts/PlanTimeSeriesChart';
import { useSiteTime } from '../../../hooks/useSiteTime';
import PhaseBandLegend from '../../../components/charts/PhaseBandLegend';
import {
  NarrationCard,
  SeriesLegend,
  accentFor,
  formatWithUnit,
  isFigure,
} from './PlanNarrationCard';
import {
  buildHourTicks,
  buildPhaseBandsOnAxis,
  formatAxisClock,
  getParameterChartSpec,
  hourlyRowIndices,
} from '../../../utils/planSeries';
import type { PlanRequestedChange } from '../../../types';

/**
 * Trailing unit suffixes on optimizer parameter names. The unit is already
 * shown in the headline figure, so repeating it in the label ("Avg chilled
 * water setpoint c") reads as a typo.
 */
const UNIT_SUFFIXES = ['_c', '_f', '_kw', '_kwh', '_pct', '_percent'];

/**
 * `avg_chilled_water_setpoint_c` → "Avg chilled water setpoint".
 *
 * The narration gives actions no display label (unlike drivers, which carry
 * `label`), so it's derived from the parameter name rather than invented via a
 * lookup table that would silently miss any new parameter.
 */
export function humanizeParameter(parameter: string): string {
  let name = parameter;
  for (const suffix of UNIT_SUFFIXES) {
    if (name.toLowerCase().endsWith(suffix) && name.length > suffix.length) {
      name = name.slice(0, -suffix.length);
      break;
    }
  }
  const words = name.replace(/[_-]+/g, ' ').trim();
  if (!words) return parameter;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Reviewer-facing order for the requested-change cards and the execution-window
 * tables: the setpoint the plan holds is read first, then the staging change it
 * makes against it. Parameters outside this list keep their upstream order,
 * after the ranked ones.
 */
const PARAMETER_ORDER = ['avg_chilled_water_setpoint_c', 'chillers_on_count'];

function parameterRank(parameter: string): number {
  const rank = PARAMETER_ORDER.indexOf(parameter);
  return rank === -1 ? PARAMETER_ORDER.length : rank;
}

interface ExecutionRow {
  time: string;
  base: string;
  rec: string;
}

interface ExecutionTable {
  key: string;
  title: string;
  unit: string;
  accent: string;
  rows: ExecutionRow[];
}

/** Table cells stay bare numbers; the unit lives once in the group header. */
function formatCell(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** One wide CSV: `Hour` plus a baseline/recommended column pair per parameter. */
function buildCsv(tables: ExecutionTable[]): string {
  if (tables.length === 0 || tables[0].rows.length === 0) return '';
  const header = [
    'Hour',
    ...tables.flatMap(t => {
      const unit = t.unit ? ` (${t.unit})` : '';
      return [`${t.title} baseline${unit}`, `${t.title} recommended${unit}`];
    }),
  ];
  const lines = [header];
  for (let r = 0; r < tables[0].rows.length; r += 1) {
    lines.push([tables[0].rows[r].time, ...tables.flatMap(t => [t.rows[r].base, t.rows[r].rec])]);
  }
  return lines.map(cols => cols.map(csvCell).join(',')).join('\r\n');
}

function PlanActionsTab() {
  const { planFile } = usePlanDetailContext();
  const actions = planFile.actions;
  const { siteZone } = useSiteTime();

  // Bands are projected onto the plan's own axis, which does not start at
  // midnight — see buildPhaseBandsOnAxis. No axis means no bands to place.
  const bands = useMemo(
    () =>
      planFile.raw_inputs
        ? buildPhaseBandsOnAxis(planFile.phases, planFile.raw_inputs.time_axis)
        : [],
    [planFile.phases, planFile.raw_inputs]
  );
  const hourTicks = useMemo(
    () => (planFile.raw_inputs ? buildHourTicks(planFile.raw_inputs.time_axis, 3, siteZone) : []),
    [planFile.raw_inputs, siteZone]
  );
  const hoverLabelAt = useMemo(
    () =>
      planFile.raw_inputs
        ? (index: number) => formatAxisClock(planFile.raw_inputs!.time_axis, index, siteZone)
        : undefined,
    [planFile.raw_inputs, siteZone]
  );

  const orderedChanges = useMemo<PlanRequestedChange[]>(
    () =>
      actions
        ? [...actions.requested_changes].sort(
            (a, b) => parameterRank(a.parameter) - parameterRank(b.parameter)
          )
        : [],
    [actions]
  );

  const executionTables = useMemo<ExecutionTable[]>(() => {
    const timeAxis = planFile.raw_inputs?.time_axis;
    if (!timeAxis) return [];
    const indices = hourlyRowIndices(timeAxis);
    return orderedChanges.flatMap((change, index) => {
      const spec = getParameterChartSpec(change.parameter, planFile.raw_inputs);
      const baseline = spec?.series.find(s => s.variant === 'baseline')?.values;
      const recommended = spec?.series.find(s => s.variant === 'recommended')?.values;
      if (!baseline || !recommended) return [];
      return [
        {
          key: `${change.equipment}-${change.parameter}-${index}`,
          title: humanizeParameter(change.parameter),
          unit: spec!.unit,
          accent: accentFor(change.parameter, index),
          rows: indices.map(i => ({
            time: formatAxisClock(timeAxis, i, siteZone),
            base: formatCell(baseline[i]),
            rec: formatCell(recommended[i]),
          })),
        },
      ];
    });
  }, [orderedChanges, planFile.raw_inputs, siteZone]);

  if (!actions) {
    return <EmptyState big="No action narration for this plan." />;
  }

  const renderChange = (change: PlanRequestedChange, index: number) => {
    const accent = accentFor(change.parameter, index);
    const spec = getParameterChartSpec(change.parameter, planFile.raw_inputs);
    const isComparison = (spec?.series.length ?? 0) > 1;
    const label = humanizeParameter(change.parameter);

    const hasFigures = isFigure(change.baseline_value) || isFigure(change.recommended_value);

    const blocks = [{ label: "What we're asking", text: change.what_were_asking }];
    if (change.why_its_safe_to_approve) {
      blocks.push({ label: "Why it's safe to approve", text: change.why_its_safe_to_approve });
    }

    return (
      <NarrationCard
        key={`${change.equipment}-${change.parameter}-${index}`}
        accent={accent}
        label={label}
        sublabel={change.equipment}
        // One figure still tells the reviewer something ("68 °F → —"); neither
        // does not, so the headline is dropped rather than reading "— → —".
        headline={
          hasFigures
            ? `${formatWithUnit(change.baseline_value, change.unit ?? '')} → ${formatWithUnit(
                change.recommended_value,
                change.unit ?? ''
              )}`
            : null
        }
        blocks={blocks}
        chart={
          spec && (
            <PlanTimeSeriesChart
              series={spec.series}
              unit={spec.unit}
              accent={accent}
              bands={bands}
              hourTicks={hourTicks}
              hoverLabelAt={hoverLabelAt}
              ariaLabel={`${label} on ${change.equipment}: baseline versus recommended over the day`}
              legend={isComparison ? <SeriesLegend accent={accent} /> : undefined}
            />
          )
        }
      />
    );
  };

  const handleExportCsv = () => {
    const csv = buildCsv(executionTables);
    if (!csv || typeof URL.createObjectURL !== 'function') return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(planFile.site_id ?? 'plan').toLowerCase()}-execution-window-24h.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pd-stack">
      <p className="pd-narrative">{actions.narrative}</p>

      <PhaseBandLegend bands={bands} />

      <section>
        <div className="pd-columns">
          <div className="pd-column">
            <h3 className="pd-column-title">Recommended vs. baseline</h3>
            {orderedChanges.length > 0 ? (
              orderedChanges.map(renderChange)
            ) : (
              <EmptyState big="This plan requests no equipment changes." />
            )}
          </div>

          {executionTables.length > 0 && (
            <div className="pd-exec">
              <div className="pd-exec-head">
                <div>
                  <h3 className="pd-exec-title">Execution-window detail</h3>
                  <p className="pd-exec-sub">Full-day hourly baseline vs. recommended</p>
                </div>
                <button type="button" className="pd-exec-export" onClick={handleExportCsv}>
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M8 2v8M4.5 6.5L8 10l3.5-3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2.5 12.5h11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  Export 24-hour CSV
                </button>
              </div>

              {executionTables.map(table => (
                <div key={table.key} className="pd-exec-group">
                  <div className="pd-exec-group-head">
                    <span
                      className="pd-exec-dot"
                      style={{ background: table.accent }}
                      aria-hidden
                    />
                    <span className="pd-exec-group-title">{table.title}</span>
                    {table.unit && <span className="pd-exec-group-unit">{table.unit}</span>}
                  </div>
                  <div className="pd-exec-scroll">
                    <table className="pd-exec-table">
                      <thead>
                        <tr>
                          <th>Hour</th>
                          <th className="pd-exec-num">Baseline</th>
                          <th className="pd-exec-num">Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.rows.map((row, i) => (
                          <tr key={`${table.key}-${i}`}>
                            <td className="pd-exec-time">{row.time}</td>
                            <td className="pd-exec-num pd-exec-base">{row.base}</td>
                            <td className="pd-exec-num" style={{ color: table.accent }}>
                              {row.rec}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default PlanActionsTab;
